import { format, parseISO, type Locale } from 'date-fns';
import moment from 'moment-timezone';

export interface TimeFrameDateRange {
  start: string; // YYYY-MM-DD format
  end: string; // YYYY-MM-DD format
  startUTC: string; // ISO UTC string for API
  endUTC: string; // ISO UTC string for API
}

/**
 * Convert a date string to UTC ISO string adjusted for building timezone
 * @param dateString - Date string in YYYY-MM-DD format
 * @param timezone - Building timezone (e.g., 'America/New_York')
 * @param isEndOfDay - If true, set to end of day (23:59:59), otherwise start of day (00:00:00)
 * @returns ISO string in UTC
 */
export const convertToUTCISO = (dateString: string, timezone: string, isEndOfDay = false): string => {
  // Parse the date string as if it's in the building timezone
  // This ensures "2025-12-16" means December 16 in the building's timezone, not browser's
  const buildingMoment = moment.tz(dateString, timezone);

  if (isEndOfDay) {
    buildingMoment.endOf('day');
  } else {
    buildingMoment.startOf('day');
  }

  // Convert to UTC and return ISO string
  return buildingMoment.utc().toISOString();
};

/**
 * Calculate date range for a time frame preset
 * @param timeFrame - Preset identifier (e.g., 'today', 'thisWeek', etc.)
 * @param timezone - Building timezone to determine "today" correctly
 * @returns Date range as YYYY-MM-DD strings in the building timezone
 */
/**
 * The week the READER's locale considers current. moment's `startOf('week')` reads its global locale,
 * which is `en`, so it returned a Sunday-Saturday week for everyone — wrong for 13 of the 16 shipped
 * catalogues, which start the week on Monday. `weekStartsOn` comes from the date-fns locale so this
 * agrees with the <Calendar> the preset sits beside.
 */
const startOfLocaleWeek = (m: moment.Moment, weekStartsOn: number): moment.Moment =>
  m
    .clone()
    .subtract((m.day() - weekStartsOn + 7) % 7, 'days')
    .startOf('day');

const calculateDateRange = (
  timeFrame: string,
  timezone: string,
  weekStartsOn: number,
): { start: string; end: string } => {
  // Get current date/time in the building's timezone
  const nowInBuildingTz = moment.tz(timezone);

  switch (timeFrame) {
    case 'today': {
      const today = nowInBuildingTz.format('YYYY-MM-DD');
      return { start: today, end: today };
    }
    case 'yesterday': {
      const yesterday = nowInBuildingTz.clone().subtract(1, 'day').format('YYYY-MM-DD');
      return { start: yesterday, end: yesterday };
    }
    case 'thisWeek': {
      const weekStart = startOfLocaleWeek(nowInBuildingTz, weekStartsOn);
      return {
        start: weekStart.format('YYYY-MM-DD'),
        end: weekStart.clone().add(6, 'days').format('YYYY-MM-DD'),
      };
    }
    case 'lastWeek': {
      const weekStart = startOfLocaleWeek(nowInBuildingTz, weekStartsOn).subtract(1, 'week');
      return {
        start: weekStart.format('YYYY-MM-DD'),
        end: weekStart.clone().add(6, 'days').format('YYYY-MM-DD'),
      };
    }
    case 'thisMonth': {
      const startOfMonth = nowInBuildingTz.clone().startOf('month').format('YYYY-MM-DD');
      const endOfMonth = nowInBuildingTz.clone().endOf('month').format('YYYY-MM-DD');
      return { start: startOfMonth, end: endOfMonth };
    }
    case 'lastMonth': {
      const lastMonth = nowInBuildingTz.clone().subtract(1, 'month');
      const startOfMonth = lastMonth.clone().startOf('month').format('YYYY-MM-DD');
      const endOfMonth = lastMonth.clone().endOf('month').format('YYYY-MM-DD');
      return { start: startOfMonth, end: endOfMonth };
    }
    case 'nextWeek': {
      const weekStart = startOfLocaleWeek(nowInBuildingTz, weekStartsOn).add(1, 'week');
      return {
        start: weekStart.format('YYYY-MM-DD'),
        end: weekStart.clone().add(6, 'days').format('YYYY-MM-DD'),
      };
    }
    case 'nextMonth': {
      const nextMonth = nowInBuildingTz.clone().add(1, 'month');
      const startOfMonth = nextMonth.clone().startOf('month').format('YYYY-MM-DD');
      const endOfMonth = nextMonth.clone().endOf('month').format('YYYY-MM-DD');
      return { start: startOfMonth, end: endOfMonth };
    }
    case 'allUpcoming': {
      const today = nowInBuildingTz.format('YYYY-MM-DD');
      const tenYearsFromNow = nowInBuildingTz.clone().add(10, 'years').endOf('year').format('YYYY-MM-DD');
      return { start: today, end: tenYearsFromNow };
    }
    case 'allPast': {
      const tenYearsAgo = nowInBuildingTz.clone().subtract(10, 'years').startOf('year').format('YYYY-MM-DD');
      const today = nowInBuildingTz.format('YYYY-MM-DD');
      return { start: tenYearsAgo, end: today };
    }
    default: {
      const today = nowInBuildingTz.format('YYYY-MM-DD');
      return { start: today, end: today };
    }
  }
};

/**
 * Get date range for a given time frame preset
 * @param timeFrame - Preset identifier (e.g., 'today', 'thisWeek', etc.)
 * @param timezone - Building timezone
 * @returns Date range with both YYYY-MM-DD format and UTC ISO strings
 */
export const getTimeFrameDateRange = (
  timeFrame: string,
  timezone: string,
  // 0 = Sunday. Defaulted so the machine-only callers stay unchanged, but the UI must pass the
  // reader's week start or "this week" disagrees with the calendar.
  weekStartsOn = 0,
): TimeFrameDateRange => {
  const { start: startDate, end: endDate } = calculateDateRange(timeFrame, timezone, weekStartsOn);

  // Convert to UTC ISO strings for API
  // These date strings are already in YYYY-MM-DD format and represent dates in the building timezone
  const startUTC = convertToUTCISO(startDate, timezone, false);
  const endUTC = convertToUTCISO(endDate, timezone, true);

  return {
    start: startDate,
    end: endDate,
    startUTC,
    endUTC,
  };
};

/**
 * Format date string for display in button.
 *
 * `PP` rather than a literal `MMM d, yyyy`: the token string forced American month-first order on every
 * reader, while `PP` is the locale's own medium date. In en-US the two are identical (`Jan 15, 2024`),
 * so nothing changes for English while de reads `15. Jan. 2024` and hu `2024. jan. 15.`.
 *
 * @param dateString - Date in YYYY-MM-DD format
 * @param locale - date-fns Locale; omitted means en-US, matching formatDateRangeForDisplay
 */
export const formatDateForDisplay = (dateString: string, locale?: Locale): string => {
  try {
    const date = parseISO(dateString);
    return format(date, 'PP', { locale });
  } catch {
    return dateString;
  }
};

/**
 * Format date range for display in button
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format (optional)
 * @returns Formatted date range string (e.g., "Jan 1, 2026" or "Jan 1 - Jan 31, 2026")
 */
export const formatDateRangeForDisplay = (startDate: string, endDate?: string | null, locale?: Locale): string => {
  try {
    const start = parseISO(startDate);

    if (!endDate || endDate === startDate) {
      return format(start, 'PP', { locale });
    }

    const end = parseISO(endDate);

    /*
     * Intl.formatRange rather than `${start} - ${end}`. It collapses the shared parts the way each
     * locale actually does it and supplies the locale's own separator:
     *
     *   en     Sep 1 – 10, 2026        fr-FR  1–10 sept. 2026
     *   en-GB  1–10 Sept 2026          hu-HU  2026. szept. 1–10.
     *
     * The previous version hand-rolled this with `locale === enUS ? 'MMM d' : 'd MMM'` — a two-bucket
     * guess that treated every non-American locale as day-first. Hungarian is year-first, so it
     * produced the mixed-order `1 szept. - 2026. szept. 10.`
     */
    return new Intl.DateTimeFormat(locale?.code, { year: 'numeric', month: 'short', day: 'numeric' }).formatRange(
      start,
      end,
    );
  } catch {
    return startDate;
  }
};
